import { ReactElement, ReactNode, useCallback, useState } from 'react';
import { MdOutlineAdd, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { Key } from '@react-types/shared';
import {
  Button,
  Listbox,
  ListboxItemProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  Selection,
} from '@ds';
import { CreateNewItem } from '@features/components/CaseFormBlocks/components/types/newItem';

const triggerBaseClasses =
  'flex flex-1 w-full items-center gap-2 bg-base-60 data-[hover=true]:bg-base-50 px-3 h-8 rounded-lg';

export type ListboxItemHandlersType = Pick<ListboxItemProps, 'onPress'>;

type SearchableListboxProps = {
  setSearchQuery: (term: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  getTriggerContent: () => ReactNode;
  items: object[];
  selectedKeys: 'all' | Iterable<Key>;
  renderItem: (item: object, handlers: ListboxItemHandlersType) => ReactElement;
  onselectionchange?: (keys: Selection) => void;
  createNewItem?: CreateNewItem;
  isClearable?: boolean;
  onClear?: () => void;
  isEmpty?: boolean;
};

export const SearchableListbox = ({
  setSearchQuery,
  disabled,
  invalid,
  getTriggerContent,
  items,
  selectedKeys,
  renderItem,
  onselectionchange,
  createNewItem,
  isClearable = false,
  onClear,
  isEmpty = true,
}: SearchableListboxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setSearchQuery('');
    }
    setIsOpen(open);
  };

  const onCreateNewItem = useCallback(() => {
    if (!createNewItem) {
      return;
    }
    setIsOpen(false);
    createNewItem.handler();
  }, [createNewItem]);

  const handlePopoverClose = () => setIsOpen(false);

  return (
    <Popover
      isOpen={isOpen}
      placement="bottom-start"
      size="md"
      triggerType="listbox"
      triggerScaleOnOpen={false}
      offset={8}
      onOpenChange={onOpenChange}
      classNames={{
        content: 'bg-base-60 py-2 px-1 rounded-md items-stretch',
      }}
    >
      <PopoverTrigger>
        <Button
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disableRipple
          isDisabled={disabled}
          variant="light"
          className={`${triggerBaseClasses} ${invalid ? ' border-1 border-danger-30' : ''}`}
        >
          <div className="flex flex-1 items-center gap-2 truncate text-base-30">{getTriggerContent()}</div>
          {isClearable && isHovered && !isEmpty && <IoMdCloseCircleOutline onClick={onClear} size={22} />}
          <MdOutlineKeyboardArrowDown size={24} className={isOpen ? 'origin-center rotate-180' : ''} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex gap-6 items-center min-w-[290px] w-full scroll">
        <div className="w-full">
          <SearchInput autoFocus placeholder="Search" onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="w-full max-h-56 overflow-auto">
          {items.length ? (
            <Listbox
              aria-label="Contacts list"
              selectionMode="single"
              selectedKeys={selectedKeys}
              hideEmptyContent
              onSelectionChange={onselectionchange}
              classNames={{ base: 'p-0' }}
            >
              {/* @ts-expect-error HeroUI v2 CollectionChildren type incompatible with React 19 ReactElement */}
              {items.map((item) => renderItem(item, { onPress: handlePopoverClose }))}
            </Listbox>
          ) : (
            <div className="text-m-m">No results found.</div>
          )}
          {createNewItem && (
            <Button
              onClick={onCreateNewItem}
              variant="light"
              className="w-full justify-start"
              size="sm"
              startContent={<MdOutlineAdd size={18} />}
            >
              {createNewItem.title}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
